import { useState, useEffect } from 'react'
import { useDeserializedFarms, useDeserializedFarmFromLpPair } from 'state/farms/useDeserializedFarms'
import { useTranslation } from 'contexts/Localization'
import { Text, Flex, Rocket2, ArrowRight, Skeleton, InfoIcon, useTooltip } from 'vvs-uikit'
import BigNumber from 'bignumber.js'
import { TokenPairImage } from 'components/TokenImage'
import styled, { useTheme } from 'styled-components'
import { usePriceVvsUsdc } from 'state/farms/hooks'
import { useFarmTokensPricesUsdc } from 'state/farms/useFarmTokensPricesUsdc'
import {
  calcPotentialFarmBoostFactor,
  PotentialFarmBoostFactorResult,
} from 'state/farmBoost/calcPotentialFarmBoostFactor'
import { getFarmAprByVersion } from 'utils/apr'
import { getAddress } from 'utils/addressHelpers'
import { Token } from 'vvs-sdk'
import { getLpUsdcPrice } from 'utils/farmsPriceHelpers'
import { getBalanceAmount } from 'utils/formatBalance'
import { getTotalApr, getDisplayApr, getBoostedDisplayApr } from 'views/Farms/utils'

interface BoostFarmSummaryProps {
  selectedBoostFarmIds: number[]
}

export const BoostFarmSummary = ({ selectedBoostFarmIds }: BoostFarmSummaryProps) => {
  const { farmsV1FromPid, farmsV2FromPid } = useDeserializedFarms((s) => s.value)
  const { t } = useTranslation()

  return (
    <Flex gap="24px" flexDirection="column">
      <Text small mb={2}>
        {t('LP Farm to boost')}
      </Text>
      <Flex gap="16px" flexDirection="column">
        {selectedBoostFarmIds
          .map((farmId) => farmsV1FromPid[farmId] ?? farmsV2FromPid[farmId])
          .map((farm) => {
            return <TokenPairInfo key={farm.pid} token={farm.token} quoteToken={farm.quoteToken} farmId={farm.pid} />
          })}
      </Flex>
    </Flex>
  )
}

interface TokenPairInfoProps {
  token: Token
  quoteToken: Token
  farmId: number
}

const TokenPairInfo = ({ token, quoteToken, farmId }: TokenPairInfoProps) => {
  const { t } = useTranslation()

  const [boostFactorData, setBoostFactor] = useState<PotentialFarmBoostFactorResult>(null)
  const isBoostFactorDataLoading = boostFactorData === null

  useEffect(() => {
    let latest = true
    setBoostFactor(null)
    calcPotentialFarmBoostFactor(farmId).then(async (calculatedBoostFactor) => {
      if (!latest) return

      setBoostFactor(calculatedBoostFactor)
    })

    return () => {
      latest = false
    }
  }, [farmId])

  const { targetRef, tooltip, tooltipVisible } = useTooltip(
    t(
      'Your APR% remain the same because you do not have any staked liquidity in the selected farm.  You can continue applying the boost and stake liquidity in this farm later to enjoy the boost APR%',
    ),
    { placement: 'top-end', tooltipOffset: [20, 10] },
  )

  const farm = useDeserializedFarmFromLpPair(token, quoteToken)
  const vvsPrice = usePriceVvsUsdc()
  const tokenPrices = useFarmTokensPricesUsdc((s) => s.value)
  const { colors } = useTheme()
  const totalLiquidity = new BigNumber(farm.lpTotalInQuoteToken).times(farm.quoteTokenPriceUsdc)

  const lpTokenPrice: BigNumber = getLpUsdcPrice(farm)

  const stakedLiquidityInUSD =
    farm.version === 2 && farm.migration
      ? getBalanceAmount(farm.lpTokenAmountInCraftsmanV2).times(lpTokenPrice)
      : totalLiquidity

  const { vvsRewardsApr, tokenRewardsApr, lpRewardsApr } = getFarmAprByVersion(
    farm.poolWeight,
    vvsPrice,
    totalLiquidity,
    getAddress(farm.lpAddresses),
    farm.version,
    farm.rewardData,
    stakedLiquidityInUSD,
    tokenPrices,
  )
  const apr = getTotalApr(tokenRewardsApr, lpRewardsApr)
  return (
    <Flex justifyContent="space-between">
      <Flex flex={1} gap="16px">
        <TokenPairImage primaryToken={token} secondaryToken={quoteToken} variant="inverted" width={32} height={32} />
        <TokenPairLabel>{farm.lpSymbol.split(' ')[0]}</TokenPairLabel>
      </Flex>
      <TokenPairLabel>
        <Flex columnGap="4px" alignItems="center">
          <Rocket2 width={16} height={16} color={colors.text} />
          <span data-testid="token-pair-apr-from">
            {apr === 0 ? <Skeleton width="50px" /> : `${getDisplayApr(tokenRewardsApr, lpRewardsApr, 1)}%`}
          </span>
          <ArrowRight width={16} height={16} color={colors.text} />
          <span data-testid="token-pair-apr-to">
            {apr === 0 || isBoostFactorDataLoading ? (
              <Skeleton width="50px" />
            ) : (
              `${getBoostedDisplayApr(
                vvsRewardsApr,
                tokenRewardsApr,
                lpRewardsApr,
                boostFactorData?.boostFactor,
                1,
                1,
              )}%`
            )}
          </span>
          {!isBoostFactorDataLoading && boostFactorData?.boostFactor === 1 && (
            <Flex ref={targetRef}>
              <InfoIcon color="primary" />
              {tooltipVisible && tooltip}
            </Flex>
          )}
        </Flex>
      </TokenPairLabel>
    </Flex>
  )
}

const TokenPairLabel = styled(Text)`
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`
